import { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "@clerk/react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Attaches the Clerk session token to every API call; renders children once wired.
export const AuthBridge = ({ children }) => {
  const { getToken } = useAuth();
  const [ready, setReady] = useState(false);
  useEffect(() => {
    const id = axios.interceptors.request.use(async (cfg) => {
      if (cfg.url?.startsWith(API)) {
        const token = await getToken();
        if (token) cfg.headers.Authorization = `Bearer ${token}`;
      }
      return cfg;
    });
    setReady(true);
    return () => axios.interceptors.request.eject(id);
  }, [getToken]);
  return ready ? children : null;
};

export const useMe = () =>
  useQuery({ queryKey: ["me"], queryFn: async () => (await axios.get(`${API}/me`)).data, retry: 1 });

export const useDashboard = () =>
  useQuery({ queryKey: ["dashboard"], queryFn: async () => (await axios.get(`${API}/dashboard`)).data });

export const useRefresh = () => {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: ["dashboard"] });
};

export const downloadExport = async () => {
  try {
    const res = await axios.get(`${API}/export`, { responseType: "blob" });
    const name = /filename="([^"]+)"/.exec(res.headers["content-disposition"] || "")?.[1] || "Amazon export.xlsx";
    const url = URL.createObjectURL(res.data);
    Object.assign(document.createElement("a"), { href: url, download: name }).click();
    URL.revokeObjectURL(url);
  } catch (e) {
    toast.error("Export failed: " + e.message);
  }
};

export const patchProduct = (code, body) => axios.patch(`${API}/products/${encodeURIComponent(code)}`, body);
export const createProduct = (body) => axios.post(`${API}/products`, body);
export const deleteProduct = (code) => axios.delete(`${API}/products/${encodeURIComponent(code)}`);
export const patchEntry = (key, body) => axios.patch(`${API}/report/${encodeURIComponent(key)}`, body);
export const patchSettings = (body) => axios.patch(`${API}/settings`, body);
export const setUserRole = (userId, role) => axios.patch(`${API}/users/${encodeURIComponent(userId)}/role`, { role });
export const errMsg = (e) => e?.response?.data?.detail?.toString?.() || e.message;
